import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import { useSnackbar } from "../components/AlertNotification";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import { SOURCE_CLASSES, isClassMap } from "../components/sourceAudio";
import s from "./Setting.module.scss";

// Which sources keep their audio (TT-1190, backend TT-1187). The server
// answers with every class; a response that doesn't is a failed load.
const AudioRetention = () => {
  const { userInfo } = useContext(RootContext);
  const userId = userInfo?.username;
  const { showSnackbar, snackbarEl } = useSnackbar();

  const [classes, setClasses] = useState(null); // a valid class map, or null
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({}); // class key -> PUT in flight

  // The keys whose save is in flight, for a re-read that lands mid-save.
  const savingRef = useRef(new Set());

  // `quiet` re-reads behind switches already showing: it never swaps them for
  // "Loading…" or, when the read fails, for the load error, and it leaves a
  // switch whose own save is still in flight alone.
  const load = useCallback(
    async ({ quiet = false } = {}) => {
      if (!userId) return;
      if (!quiet) setLoading(true);
      const res = await AppBackService.getMediaSettings(userId);
      const fresh = res && !res.error && isClassMap(res.classes) ? res.classes : null;
      if (!quiet) {
        setClasses(fresh);
      } else if (fresh) {
        setClasses((prev) => {
          const merged = { ...fresh };
          savingRef.current.forEach((key) => {
            if (prev) merged[key] = prev[key];
          });
          return merged;
        });
      }
      setLoading(false);
    },
    [userId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Optimistic: flip now; on any doubt (a refused save, an odd reply) put the
  // switch back and re-read the server rather than guess. Each switch holds
  // still while its own save is in flight, and only its key is taken from the
  // reply, so two quick toggles can't overwrite each other.
  const toggle = useCallback(
    async (key, retain) => {
      const setKey = (value) => setClasses((prev) => (prev ? { ...prev, [key]: value } : prev));
      setKey(retain);
      savingRef.current.add(key);
      setSaving((prev) => ({ ...prev, [key]: true }));
      const res = await AppBackService.updateMediaSettings(userId, { [key]: retain });
      savingRef.current.delete(key);
      if (res && !res.error && isClassMap(res.classes)) {
        setKey(res.classes[key]);
      } else {
        if (!res || res.error) showSnackbar("Couldn't save that change.", "error");
        setKey(!retain);
        await load({ quiet: true });
      }
      setSaving((prev) => ({ ...prev, [key]: false }));
    },
    [load, showSnackbar, userId]
  );

  return (
    <>
      <div className={s.sectionTitle}>Audio Recordings</div>
      <div className={s.sectionText}>
        Keep the original audio of new documents, by source. Turning a source off applies to documents created after the
        change; audio already kept stays until you delete it from its document.
      </div>
      {loading ? (
        <div className={s.sectionText}>Loading…</div>
      ) : !classes ? (
        <>
          <div className={s.errorMsg}>Couldn't load your audio settings.</div>
          <button type="button" className={s.retryLink} onClick={() => load()}>
            Retry
          </button>
        </>
      ) : (
        <div className={s.switchList}>
          {SOURCE_CLASSES.map(({ key, label }) => (
            <FormControlLabel
              key={key}
              label={label}
              control={
                <Switch
                  checked={classes[key]}
                  disabled={!!saving[key]}
                  onChange={(e) => toggle(key, e.target.checked)}
                />
              }
            />
          ))}
        </div>
      )}
      {snackbarEl}
    </>
  );
};

export default AudioRetention;
